import React, { useState, useRef } from "react";
import { FRONT_POINTS, BACK_POINTS, isSunnahPoint } from "../lib/hijamaCoords";
import { Download, Printer } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

// Stylized human silhouette - front view
const FrontSilhouette = () => (
  <g fill="none" stroke="#2C5545" strokeWidth="1.4" opacity="0.5">
    {/* Head */}
    <ellipse cx="100" cy="38" rx="22" ry="28" />
    {/* Neck */}
    <path d="M88 64 L88 78 Q88 90 100 92 Q112 90 112 78 L112 64" />
    {/* Torso */}
    <path d="M62 95 Q60 100 65 108 L70 130 L65 200 L72 280 L85 285 L100 282 L115 285 L128 280 L135 200 L130 130 L135 108 Q140 100 138 95 Q130 92 112 92 L88 92 Q70 92 62 95 Z" />
    {/* Arms */}
    <path d="M62 100 Q40 130 38 180 L40 240 L42 290 L48 295" />
    <path d="M138 100 Q160 130 162 180 L160 240 L158 290 L152 295" />
    {/* Legs */}
    <path d="M75 282 Q72 320 78 360 L82 420 L85 470 L82 495 L88 500 L92 470 L92 360 Q95 320 92 282" />
    <path d="M125 282 Q128 320 122 360 L118 420 L115 470 L118 495 L112 500 L108 470 L108 360 Q105 320 108 282" />
  </g>
);

// Stylized human silhouette - back view
const BackSilhouette = () => (
  <g fill="none" stroke="#2C5545" strokeWidth="1.4" opacity="0.5">
    {/* Head */}
    <ellipse cx="100" cy="38" rx="22" ry="28" />
    {/* Neck + trapezius */}
    <path d="M82 70 Q85 88 95 95 L105 95 Q115 88 118 70" />
    {/* Torso back */}
    <path d="M62 100 Q70 92 88 92 L112 92 Q130 92 138 100 Q142 110 138 130 L132 200 L135 280 L120 290 L100 288 L80 290 L65 280 L68 200 L62 130 Q58 110 62 100 Z" />
    {/* Arms */}
    <path d="M62 100 Q42 130 40 180 L42 240 L44 290" />
    <path d="M138 100 Q158 130 160 180 L158 240 L156 290" />
    {/* Legs */}
    <path d="M80 290 Q78 320 82 360 L85 420 L88 470 L86 495" />
    <path d="M120 290 Q122 320 118 360 L115 420 L112 470 L114 495" />
    {/* Spine indication */}
    <line x1="100" y1="100" x2="100" y2="270" strokeDasharray="2 2" opacity="0.4" />
  </g>
);

const PointDot = ({ point, id, highlighted, sunnah, onHover, t }) => {
  const baseColor = highlighted ? "#B68D40" : "#2C5545";
  const fillColor = highlighted ? "#B68D40" : "#FFFFFF";
  const ring = highlighted ? 9 : 5;
  return (
    <g
      style={{ cursor: "pointer" }}
      onMouseEnter={() => onHover?.({ id, ...point })}
      onMouseLeave={() => onHover?.(null)}
    >
      {highlighted && (
        <circle
          cx={point.x}
          cy={point.y}
          r={ring + 3}
          fill="none"
          stroke={baseColor}
          strokeWidth="1.5"
          opacity="0.4"
          className="animate-gentle-pulse"
        />
      )}
      <circle
        cx={point.x}
        cy={point.y}
        r={ring}
        fill={fillColor}
        stroke={baseColor}
        strokeWidth="1.5"
      />
      {highlighted && (
        <text
          x={point.x}
          y={point.y + 3}
          textAnchor="middle"
          fontSize="7"
          fontWeight="700"
          fill="#FDFBF7"
          pointerEvents="none"
        >
          {id}
        </text>
      )}
      {sunnah && (
        <text
          x={point.x + 10}
          y={point.y - 6}
          fontSize="9"
          fill="#B68D40"
          pointerEvents="none"
        >
          ★
        </text>
      )}
    </g>
  );
};

const BodyDiagram = ({ highlightedPoints = [], illnessName = "" }) => {
  const [hover, setHover] = useState(null);
  const containerRef = useRef(null);
  const { t } = useLanguage();

  const highlightSet = new Set(highlightedPoints);

  const handlePrint = () => {
    window.print();
  };

  const visibleFront = Object.entries(FRONT_POINTS).filter(
    ([id]) => highlightSet.has(Number(id)) || Object.keys(FRONT_POINTS).indexOf(id) < 100
  );
  const visibleBack = Object.entries(BACK_POINTS).filter(
    ([id]) => highlightSet.has(Number(id)) || Object.keys(BACK_POINTS).indexOf(id) < 100
  );

  return (
    <div
      ref={containerRef}
      data-testid="body-diagram"
      className="clay-surface rounded-3xl p-6 sm:p-8 print:break-inside-avoid"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold mb-1">
            {t("diagram_label")}
          </p>
          <h3 className="font-heading text-xl text-forest font-semibold">
            {t("diagram_title")}
          </h3>
        </div>
        <button
          data-testid="print-diagram-btn"
          onClick={handlePrint}
          className="flex items-center gap-2 bg-cream hover:bg-white border border-forest/10 text-forest rounded-full px-4 py-2 text-xs font-medium transition"
        >
          <Printer className="w-3.5 h-3.5" />
          {t("print_protocol")}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-6">
        {/* FRONT VIEW */}
        <div className="relative">
          <p className="text-center text-[10px] uppercase tracking-[0.2em] text-mute mb-2 font-medium">
            {t("front_view")}
          </p>
          <div className="rounded-2xl bg-cream/40 p-2 sm:p-4 border border-forest/10">
            <svg
              viewBox="0 0 200 520"
              className="w-full h-auto"
              data-testid="body-front-svg"
            >
              <FrontSilhouette />
              {visibleFront.map(([id, point]) => (
                <PointDot
                  key={id}
                  id={Number(id)}
                  point={point}
                  highlighted={highlightSet.has(Number(id))}
                  sunnah={isSunnahPoint(Number(id))}
                  onHover={setHover}
                  t={t}
                />
              ))}
            </svg>
          </div>
        </div>

        {/* BACK VIEW */}
        <div className="relative">
          <p className="text-center text-[10px] uppercase tracking-[0.2em] text-mute mb-2 font-medium">
            {t("back_view")}
          </p>
          <div className="rounded-2xl bg-cream/40 p-2 sm:p-4 border border-forest/10">
            <svg
              viewBox="0 0 200 520"
              className="w-full h-auto"
              data-testid="body-back-svg"
            >
              <BackSilhouette />
              {visibleBack.map(([id, point]) => (
                <PointDot
                  key={id}
                  id={Number(id)}
                  point={point}
                  highlighted={highlightSet.has(Number(id))}
                  sunnah={isSunnahPoint(Number(id))}
                  onHover={setHover}
                  t={t}
                />
              ))}
            </svg>
          </div>
        </div>
      </div>

      {/* Hover info / legend */}
      <div className="mt-5 flex flex-wrap items-center gap-3 text-xs">
        {hover ? (
          <div className="flex items-center gap-2 bg-forest text-sand rounded-full px-4 py-2">
            <span className="font-semibold">#{hover.id}</span>
            <span>{hover.label}</span>
          </div>
        ) : (
          <p className="text-mute italic">{t("hover_hint")}</p>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-gold border border-gold" />
            <span className="text-mute">{t("legend_treatment")}</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="text-gold">★</span>
            <span className="text-mute">{t("legend_sunnah")}</span>
          </span>
        </div>
      </div>

      {highlightedPoints.length > 0 && (
        <div className="mt-6 pt-5 border-t border-forest/10">
          <p className="text-xs uppercase tracking-[0.2em] text-gold mb-3">
            {t("recommended_points")} ({highlightedPoints.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {highlightedPoints.map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1.5 bg-forest text-sand rounded-full px-3 py-1 text-xs font-medium"
              >
                #{id}
                {isSunnahPoint(id) && <span className="text-gold">★</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      <p className="text-[10px] text-mute italic mt-5">
        {t("diagram_disclaimer")}
      </p>
    </div>
  );
};

export default BodyDiagram;
